import {
  SCHEDULE_LABELS,
  type ScraperDetail,
  type ScraperInput,
  type ScraperRunSummary,
  type ScraperSummary,
  safeHref,
} from '@hoopoe/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { ArrowLeft, Globe, History } from 'lucide-react';
import { useState } from 'react';
import { useReauth } from '../components/auth/reauth';
import { DataNumber, DataText } from '../components/data/data-text';
import { EmptyState } from '../components/guidance/empty-state';
import { ErrorState } from '../components/guidance/error-state';
import { PageHeader } from '../components/layout/app-shell';
import { MonitorForm } from '../components/scrapers/monitor-form';
import { MonitorStatus, when } from '../components/scrapers/monitor-status';
import { Button } from '../components/ui/button';
import { useOptionalConfirm } from '../components/ui/confirm';
import { ApiError, api } from '../lib/api';
import { MONITORS_KEY, monitorKey } from '../lib/scrapers';

// A website monitor (task 40): what changed on its pages (each change is a stored document, linked to the version it
// replaces), the pages it reads, and its recent checks; Check now, change, pause and remove.

export const Route = createFileRoute('/admin/scrapers/$id')({ component: MonitorPage });

const OUTCOME: Record<ScraperDetail['pages'][number]['outcome'], string> = {
  new: 'New',
  changed: 'Changed',
  unchanged: 'Unchanged',
  failed: 'Failed',
  blocked: 'Skipped (robots.txt)',
};
const RUN_STATUS: Record<ScraperRunSummary['status'], string> = {
  running: 'Checking…',
  ok: 'Done',
  partial: 'Some pages failed',
  failed: 'Failed',
};

/** Stable keys for excerpt lines (a line can repeat): the line plus its occurrence. */
function keyed(lines: string[]) {
  const seen = new Map<string, number>();
  return lines.map(text => {
    const n = (seen.get(text) ?? 0) + 1;
    seen.set(text, n);
    return { key: `${n}:${text}`, text };
  });
}

function MonitorPage() {
  const { id } = Route.useParams();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { withReauth } = useReauth();
  const confirm = useOptionalConfirm();
  const q = useQuery({ queryKey: monitorKey(id), queryFn: () => api<ScraperDetail>(`/admin/scrapers/${id}`) });
  const settings = useQuery({
    queryKey: ['admin', 'scraper-settings'],
    queryFn: () => api<{ enabled: boolean; browserAvailable: boolean }>('/admin/scraper-settings'),
  });
  const [editing, setEditing] = useState(false);
  const [message, setMessage] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
  const onError = (e: unknown) => {
    if (e instanceof ApiError && e.code === 'reauth_cancelled') return;
    setMessage({ kind: 'error', text: e instanceof ApiError ? e.message : 'Something went wrong.' });
  };
  const refresh = () => {
    qc.invalidateQueries({ queryKey: monitorKey(id) });
    qc.invalidateQueries({ queryKey: MONITORS_KEY, exact: true });
  };

  const run = useMutation({
    mutationFn: () => withReauth(() => api<{ run: ScraperRunSummary }>(`/admin/scrapers/${id}/run`, { body: {} })),
    onMutate: () => setMessage(null),
    onSuccess: ({ run: r }) => {
      const changed = r.pagesNew + r.pagesChanged;
      setMessage({
        kind: r.status === 'failed' ? 'error' : 'ok',
        text:
          r.status === 'failed'
            ? `The check failed: ${r.error ?? 'no page could be read.'}`
            : `Checked ${r.pagesChecked} ${r.pagesChecked === 1 ? 'page' : 'pages'}: ${
                changed ? `${changed} new or changed, stored in the Library.` : 'no change.'
              }${r.pagesFailed ? ` ${r.pagesFailed} could not be read.` : ''}`,
      });
    },
    onSettled: refresh,
    onError,
  });
  const save = useMutation({
    mutationFn: (input: ScraperInput & { enabled?: boolean }) =>
      withReauth(() => api<{ scraper: ScraperSummary }>(`/admin/scrapers/${id}`, { method: 'PATCH', body: input })),
    onSuccess: (r, input) => {
      setEditing(false);
      setMessage({
        kind: 'ok',
        text:
          input.enabled === false
            ? `Paused “${r.scraper.name}”. It isn’t checked on schedule until you resume it.`
            : input.enabled === true
              ? `Resumed “${r.scraper.name}”.`
              : `Saved “${r.scraper.name}”. The changes apply from its next check.`,
      });
      refresh();
    },
    onError,
  });
  const remove = useMutation({
    mutationFn: () => withReauth(() => api(`/admin/scrapers/${id}`, { method: 'DELETE' })),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: MONITORS_KEY });
      navigate({ to: '/admin/scrapers' });
    },
    onError,
  });

  if (q.isError)
    return (
      <>
        <PageHeader title="Website monitor" />
        <ErrorState
          error={q.error}
          title="This website monitor couldn’t be loaded"
          onRetry={() => q.refetch()}
          learnMore="website-monitors"
        />
      </>
    );
  if (!q.data) return <p className="text-sm text-muted-foreground">Loading…</p>;
  const { scraper: m, runs, pages, changes } = q.data;
  const switchedOff = settings.data?.enabled === false;

  const askRemove = async () => {
    setMessage(null);
    const ok = confirm
      ? await confirm({
          title: `Remove “${m.name}”?`,
          body: 'It stops checking. The pages it stored stay in the Library, in their collection.',
          confirmLabel: 'Remove monitor',
          cancelLabel: 'Keep it',
          destructive: true,
        })
      : true;
    if (ok) remove.mutate();
  };

  return (
    <div className="space-y-8">
      <Link to="/admin/scrapers" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="size-4" aria-hidden="true" /> All website monitors
      </Link>
      <PageHeader
        title={m.name}
        description={`${SCHEDULE_LABELS[m.schedule]} · ${m.scope === 'section' ? `up to ${m.maxPages} pages` : 'one page'}`}
      />
      <p className="text-sm">
        <a
          href={safeHref(m.url)}
          target="_blank"
          rel="noopener noreferrer nofollow"
          className="break-all underline underline-offset-2"
          dir="ltr"
        >
          {m.url}
        </a>
      </p>
      <div className="flex flex-wrap items-center gap-2 text-sm" data-testid="monitor-status">
        <MonitorStatus monitor={m} />
        <span className="text-muted-foreground">
          · Next check: <DataNumber>{m.enabled ? (m.nextRunAt ? when(m.nextRunAt) : 'by hand only') : 'paused'}</DataNumber>
        </span>
      </div>
      <div className="flex flex-wrap gap-2">
        <Button disabled={run.isPending || m.running || switchedOff} onClick={() => run.mutate()}>
          {run.isPending || m.running ? 'Checking…' : 'Check now'}
        </Button>
        <Button variant="outline" onClick={() => setEditing(e => !e)} aria-expanded={editing}>
          Change settings
        </Button>
        <Button variant="outline" disabled={save.isPending} onClick={() => save.mutate({ enabled: !m.enabled })}>
          {m.enabled ? 'Pause' : 'Resume'}
        </Button>
        <Button variant="ghost" onClick={askRemove} disabled={remove.isPending}>
          Remove
        </Button>
      </div>
      {switchedOff && (
        <p role="status" className="rounded-md border border-warning bg-surface px-3 py-2 text-sm">
          Website monitors are switched off for everyone. Switch them on in Admin → Website monitors to check.
        </p>
      )}
      {run.isPending && (
        <p role="status" className="text-sm text-muted-foreground">
          Reading the {m.scope === 'section' ? 'pages' : 'page'}… This can take a minute; progress also shows in Activity.
        </p>
      )}
      {message && (
        <p
          role={message.kind === 'error' ? 'alert' : 'status'}
          className={message.kind === 'error' ? 'text-sm text-danger-foreground' : 'text-sm'}
        >
          {message.text}
        </p>
      )}

      {editing && (
        <section aria-labelledby="edit-title" className="space-y-3">
          <h2 id="edit-title" className="font-semibold">
            Change “<DataText>{m.name}</DataText>”
          </h2>
          <MonitorForm
            monitor={m}
            titleId="edit-title"
            pending={save.isPending}
            browserAvailable={settings.data?.browserAvailable ?? true}
            onSubmit={input => {
              setMessage(null);
              save.mutate(input);
            }}
            onCancel={() => setEditing(false)}
          />
        </section>
      )}

      <section aria-labelledby="changes-title" className="space-y-3">
        <h2 id="changes-title" className="font-semibold">
          Changes
        </h2>
        {changes.length === 0 ? (
          <EmptyState icon={History} title="No page stored yet" learnMore="website-monitors">
            The first check stores each page it reads. After that, a page is stored again only when its text changes.
          </EmptyState>
        ) : (
          <ol className="space-y-3" aria-label="Changes">
            {changes.map(ch => (
              <li key={ch.id} className="space-y-2 rounded-lg border bg-surface p-3 text-sm" data-testid="monitor-change">
                <div className="flex flex-wrap items-baseline gap-x-2">
                  <span className="font-medium">{ch.kind === 'new' ? 'First version' : 'Changed'}</span>
                  <Link to="/library/$id" params={{ id: ch.documentId }} className="underline underline-offset-2">
                    <DataText>{ch.title ?? ch.url}</DataText>
                  </Link>
                  <span className="text-xs text-muted-foreground">
                    <DataNumber>{when(ch.detectedAt)}</DataNumber>
                  </span>
                </div>
                <p className="break-all text-xs text-muted-foreground" dir="ltr">
                  {ch.url}
                </p>
                {ch.kind === 'changed' && (
                  <>
                    <p className="text-xs">
                      <DataNumber>{ch.added}</DataNumber> {ch.added === 1 ? 'line' : 'lines'} added, <DataNumber>{ch.removed}</DataNumber>{' '}
                      removed
                      {ch.previousDocumentId && (
                        <>
                          {' · '}
                          <Link to="/library/$id" params={{ id: ch.previousDocumentId }} className="underline underline-offset-2">
                            previous version
                          </Link>
                        </>
                      )}
                    </p>
                    {(ch.excerpt.added.length > 0 || ch.excerpt.removed.length > 0) && (
                      <ul className="space-y-1 rounded-md border bg-surface-raised p-2 text-xs" aria-label="What changed">
                        {keyed(ch.excerpt.removed).map(({ key, text: l }) => (
                          <li key={key} className="text-danger-foreground">
                            <span className="sr-only">Removed: </span>
                            <span aria-hidden="true">− </span>
                            <DataText>{l}</DataText>
                          </li>
                        ))}
                        {keyed(ch.excerpt.added).map(({ key, text: l }) => (
                          <li key={key} className="text-success-foreground">
                            <span className="sr-only">Added: </span>
                            <span aria-hidden="true">+ </span>
                            <DataText>{l}</DataText>
                          </li>
                        ))}
                      </ul>
                    )}
                  </>
                )}
              </li>
            ))}
          </ol>
        )}
      </section>

      <section aria-labelledby="pages-title" className="space-y-3">
        <h2 id="pages-title" className="font-semibold">
          Pages
        </h2>
        {pages.length === 0 ? (
          <EmptyState icon={Globe} title="Not checked yet" learnMore="website-monitors">
            Press Check now to read {m.scope === 'section' ? 'the pages' : 'the page'} for the first time.
          </EmptyState>
        ) : (
          <div className="relative overflow-x-auto rounded-lg border bg-surface">
            <table className="w-full text-sm" aria-label="Pages">
              <thead>
                <tr className="border-b text-muted-foreground">
                  <th className="p-2 text-start font-medium">Page</th>
                  <th className="p-2 text-start font-medium">Last check</th>
                  <th className="p-2 text-start font-medium">Last changed</th>
                </tr>
              </thead>
              <tbody>
                {pages.map(p => (
                  <tr key={p.url} className="border-b last:border-0">
                    <td className="max-w-md p-2">
                      {p.documentId ? (
                        <Link to="/library/$id" params={{ id: p.documentId }} className="underline underline-offset-2">
                          <DataText>{p.title ?? p.url}</DataText>
                        </Link>
                      ) : (
                        <DataText>{p.title ?? p.url}</DataText>
                      )}
                      <span className="block truncate text-xs text-muted-foreground" dir="ltr">
                        {p.url}
                      </span>
                    </td>
                    <td className="p-2">
                      <span
                        className={
                          p.outcome === 'failed' ? 'text-danger-foreground' : p.outcome === 'blocked' ? 'text-warning-foreground' : ''
                        }
                      >
                        {OUTCOME[p.outcome]}
                      </span>{' '}
                      <span className="text-xs text-muted-foreground">
                        <DataNumber>{when(p.lastCheckedAt)}</DataNumber>
                      </span>
                      {p.error && (
                        <span className="block text-xs text-muted-foreground">
                          <DataText>{p.error}</DataText>
                        </span>
                      )}
                    </td>
                    <td className="p-2">
                      <DataNumber>{when(p.lastChangedAt)}</DataNumber>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section aria-labelledby="runs-title" className="space-y-3">
        <h2 id="runs-title" className="font-semibold">
          Recent checks
        </h2>
        {runs.length === 0 ? (
          <p className="text-sm text-muted-foreground">No check yet.</p>
        ) : (
          <div className="relative overflow-x-auto rounded-lg border bg-surface">
            <table className="w-full text-sm" aria-label="Recent checks">
              <thead>
                <tr className="border-b text-muted-foreground">
                  <th className="p-2 text-start font-medium">When</th>
                  <th className="p-2 text-start font-medium">Result</th>
                  <th className="p-2 text-start font-medium">Pages</th>
                  <th className="p-2 text-start font-medium">How</th>
                </tr>
              </thead>
              <tbody>
                {runs.map(r => (
                  <tr key={r.id} className="border-b last:border-0">
                    <td className="p-2">
                      <DataNumber>{when(r.startedAt)}</DataNumber>
                    </td>
                    <td className="p-2">
                      {RUN_STATUS[r.status]}
                      {r.error && (
                        <span className="block text-xs text-muted-foreground">
                          <DataText>{r.error}</DataText>
                        </span>
                      )}
                    </td>
                    <td className="p-2">
                      <DataNumber>
                        {r.pagesChecked} checked · {r.pagesNew + r.pagesChanged} new or changed
                        {r.pagesFailed ? ` · ${r.pagesFailed} failed` : ''}
                      </DataNumber>
                    </td>
                    <td className="p-2 text-muted-foreground">
                      {r.trigger === 'manual' ? 'By hand' : 'On schedule'}, {r.rendering === 'browser' ? 'in a browser' : 'as sent'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
